import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useId,
  useState,
} from "react";

import { cx, styled } from "simplydo/react/styled";

import { ErrorLabel, StyledField } from "./Form.styles";
import type { FormFieldProps } from "./Form.types";
import {
  FormFieldContext,
  type FormFieldContextValue,
} from "./FormFieldContext";

const useFormFieldContextValue = (
  labelId: string,
  hasExternalLabel: boolean,
  required = false,
): FormFieldContextValue => {
  const [label, setLabel] = useState<{ id?: string }>();
  const [controlIds, setControlIds] = useState<string[]>([]);

  const registerLabel = useCallback((id?: string) => {
    const entry = { id };
    setLabel(entry);
    return () =>
      setLabel((current) => (current === entry ? undefined : current));
  }, []);

  const registerControl = useCallback((id: string) => {
    setControlIds((ids) => [...ids, id]);
    return () =>
      setControlIds((ids) => {
        const index = ids.indexOf(id);
        return ids.filter((_, i) => i !== index);
      });
  }, []);

  return {
    labelId: label?.id ?? labelId,
    hasLabel: hasExternalLabel || !!label,
    hasExternalLabel,
    required,
    controlId: controlIds[0],
    registerLabel,
    registerControl,
  };
};

const FormFieldComponent = ({
  children,
  className,
  style,
  disabled,
  error,
  required,
}: FormFieldProps): ReactElement => {
  const errorMessage =
    error && typeof error !== "boolean" ? (error as ReactNode) : null;
  const field = useFormFieldContextValue(useId(), false, Boolean(required));

  return (
    <FormFieldContext value={field}>
      <StyledField
        className={cx(
          className,
          Boolean(error) && "is-error",
          disabled && "is-disabled",
          Boolean(required) && "is-required",
        )}
        style={style}
      >
        {children}
        {errorMessage ? <ErrorLabel>{errorMessage}</ErrorLabel> : null}
      </StyledField>
    </FormFieldContext>
  );
};

export const FormField = styled(FormFieldComponent)``;

/** Names the first control inside it with an element rendered elsewhere. */
export const FormFieldLabelledBy = ({
  labelId,
  children,
}: {
  labelId: string;
  children?: ReactNode;
}): ReactElement => (
  <FormFieldContext value={useFormFieldContextValue(labelId, true)}>
    {children}
  </FormFieldContext>
);
